import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useLatestOrderMessageByQuoteIds } from 'src/api/db/apiOrderMessage'
import { useQuoteApi, type QuoteListRow } from 'src/api/db/apiQuote'
import { useGetOrgId, useQuoteListOrgId } from 'src/api/helpers/getOrgId'
import { paths } from 'src/routes/paths'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { seedInitialChatNote } from 'src/pages/_myAccount/Requests/Request/components/Chat/hooks'
import {
  APPROVED_PROOFS_STATUS,
  APPROVED_QUOTE_STATUS,
  DELIVERED_STATUS,
  SALES_APPROVED_QUOTE_STATUS,
  effectiveQuoteStatus,
  isDraftQuoteStatus,
} from 'src/shared/consts/quoteStatus'

export type CustomerAttentionReason =
  | 'approve_quote_and_proofs'
  | 'confirm_order'

const customerAttentionFor = (
  quote: QuoteListRow
): CustomerAttentionReason | null => {
  const status = effectiveQuoteStatus(quote.status)
  if (status === SALES_APPROVED_QUOTE_STATUS) return 'approve_quote_and_proofs'
  if (status === APPROVED_QUOTE_STATUS) {
    return (quote.quote_proof?.length ?? 0) > 0
      ? 'approve_quote_and_proofs'
      : null
  }
  if (status === APPROVED_PROOFS_STATUS) return 'confirm_order'
  return null
}

export const useDashboardOrders = () => {
  const quoteListOrgId = useQuoteListOrgId()
  const { quotes, isLoadingQuotes } = useQuoteApi({
    org_id: quoteListOrgId,
    list: {
      limit: 100,
      includeProofs: true,
      countMode: 'none',
    },
  })

  const allQuotes = quotes?.data ?? []

  const { currentOrders, previousOrders } = useMemo(() => {
    const sortByCreated = (list: typeof allQuotes) =>
      [...list].sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
      )

    return {
      currentOrders: sortByCreated(
        allQuotes.filter(
          (q) =>
            q.status !== DELIVERED_STATUS && !isDraftQuoteStatus(q.status)
        )
      ),
      previousOrders: sortByCreated(
        allQuotes.filter((q) => q.status === DELIVERED_STATUS)
      ),
    }
  }, [allQuotes])

  return { allQuotes, currentOrders, previousOrders, isLoadingQuotes }
}

export const useDashboardWaiting = (quotes: QuoteListRow[]) => {
  const quoteIds = quotes.map((quote) => quote.id)
  const { data: latestByQuoteId, isPending } =
    useLatestOrderMessageByQuoteIds(quoteIds)

  const { chatsWaiting, ordersWaiting } = useMemo(() => {
    const chats = quotes.filter((quote) => {
      if (quote.status === DELIVERED_STATUS) return false
      return latestByQuoteId?.get(quote.id)?.role === 'admin'
    })

    const orders = quotes
      .map((quote) => {
        const reason = customerAttentionFor(quote)
        if (!reason) return null
        return { quote, reason }
      })
      .filter(Boolean) as Array<{
      quote: QuoteListRow
      reason: CustomerAttentionReason
    }>

    return { chatsWaiting: chats, ordersWaiting: orders }
  }, [latestByQuoteId, quotes])

  return {
    chatsWaiting,
    ordersWaiting,
    isLoadingWaiting: quoteIds.length > 0 && isPending,
  }
}

export const useCreateOrderFromDashboardMessage = () => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { session } = useAuth()
  const org_id = useGetOrgId()
  const quoteListOrgId = useQuoteListOrgId()
  const { organizationById } = useOrgsApi({ org_id })
  const { quotes, createQuote } = useQuoteApi({ org_id: quoteListOrgId })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createOrderWithMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim()
      if (!trimmed || isSubmitting) return

      const orgId = organizationById?.data?.id
      const userId = session?.user?.id
      if (!orgId || !userId) {
        throw new Error(
          t(
            'Unable to create order. Please try again.',
            'Kunde inte skapa order. Försök igen.'
          )
        )
      }

      setIsSubmitting(true)
      try {
        const allQuotes = quotes?.data ?? []
        const nextQuoteNumber = allQuotes.length + 1
        const quoteName = `${t('Order', 'Order')} #${nextQuoteNumber}`

        const quote = await createQuote.mutateAsync({
          name: quoteName,
          org_id: orgId,
        })

        if (!quote?.public_id) {
          throw new Error(
            t(
              'Unable to create order. Please try again.',
              'Kunde inte skapa order. Försök igen.'
            )
          )
        }

        seedInitialChatNote(quote.public_id, trimmed)

        navigate(`${paths.myAccount}${paths.requests}/${quote.public_id}`, {
          state: { initialChatMessage: trimmed },
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      createQuote,
      isSubmitting,
      navigate,
      organizationById?.data?.id,
      quotes?.data,
      session?.user?.id,
      t,
    ]
  )

  return { createOrderWithMessage, isSubmitting }
}
